import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PublicLayout } from './public/PublicLayout';
import PublicCalendarRoute from './public/PublicCalendarRoute';
import PublicEventDetail from './public/PublicEventDetail';

// The admin app is loaded on demand so the public routes never pull in its
// dependency graph — in particular the Clerk sign-in SDK, which is created at
// module load (providers/clerk/clerk.ts) and can reload the page during its
// own handshake (it is the development instance today). Public visitors never
// sign in, and a reload on /calendar reset the viewed month (CU 86bcf6xjt).
// See spec pages/public/calendar.md §6.1.
const CRM = lazy(() => import('./root/CRM').then(m => ({ default: m.CRM })));

/**
 * `AppRoutes` — the `<Routes>` tree extracted from `App` so it can be
 * wrapped in a `MemoryRouter` by the Plan 12-03 routing unit test
 * (`src/__tests__/App.routing.test.tsx`). Production wraps it in
 * `BrowserRouter` via `<App />`.
 *
 * Public routes (`/calendar`, `/event/:id`) are mounted ABOVE the react-admin
 * `<Admin>` tree per D-12 so they do not trigger `authProvider.checkAuth()`
 * (Pitfall 5). The public layout deliberately does not import any react-admin
 * hooks for the same reason.
 *
 * Phase 12.1 hotfix — the public event-detail URL is now SINGULAR
 * (`/event/:id`) to avoid conflict with react-admin's plural `/events/*`
 * resource routes. The original Plan 12-03 attempt used explicit static
 * routes (`/events/create`, `/events/:id/edit`) above the public dynamic
 * route to outrank it — but those static routes ABSORB the URL path, so
 * when CRM mounts inside one of them, react-admin's relative resource
 * routes (`events/*` per ra-core/CoreAdminRoutes.tsx:108) see an empty
 * leftover and the dashboard route (`/` per CoreAdminRoutes:113) wins,
 * silently bouncing the user to `/home` via RoleAwareLanding.
 *
 * Switching the public route to a non-conflicting singular path is the
 * one fix that doesn't require a nested-BrowserRouter hack or invasive
 * react-admin patches. Any backward-compatibility for old shared
 * `/events/<uuid>` links should be added via a server-side rewrite or
 * a dedicated redirect route — NOT by re-introducing the absorbing
 * static routes.
 */
export const AppRoutes = () => (
    <Routes>
        {/* Public routes — no react-admin context, no auth redirect.
            `/calendar` chooses its own wrapper: the full public layout
            normally, or chrome-free when `?embed=1` (the iframe the charity's
            website points at). See PublicCalendarRoute. */}
        <Route path="/calendar" element={<PublicCalendarRoute />} />
        {/* Phase 12.1 hotfix — singular `/event/:id` so react-admin can own
            the plural `/events/*` namespace including `/events/create` and
            `/events/:id/edit`. */}
        <Route
            path="/event/:id"
            element={
                <PublicLayout>
                    <PublicEventDetail />
                </PublicLayout>
            }
        />
        {/* Authenticated app — react-admin owns everything else, including
            /events/create, /events/:id/edit, /events/:id/show, and the
            plural /events list. */}
        <Route
            path="/*"
            element={
                <Suspense fallback={null}>
                    <CRM />
                </Suspense>
            }
        />
    </Routes>
);

/**
 * Application entry point.
 */
const App = () => (
    <BrowserRouter>
        <AppRoutes />
    </BrowserRouter>
);

export default App;
